import { forwardRef } from "react";
import {
  Platform,
  TextInput,
  type StyleProp,
  type TextInputProps,
  type TextStyle,
} from "react-native";
import { colors, semanticColors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";

/** Layout belongs to the containing field; editable text styling belongs here. */
export type FieldInputLayoutStyle = Pick<
  TextStyle,
  | "flex"
  | "flexGrow"
  | "flexShrink"
  | "flexBasis"
  | "alignSelf"
  | "width"
  | "minWidth"
  | "maxWidth"
  | "height"
  | "minHeight"
  | "maxHeight"
  | "margin"
  | "marginHorizontal"
  | "marginVertical"
  | "marginTop"
  | "marginBottom"
  | "marginLeft"
  | "marginRight"
  | "padding"
  | "paddingHorizontal"
  | "paddingVertical"
  | "paddingTop"
  | "paddingBottom"
  | "paddingLeft"
  | "paddingRight"
  | "textAlign"
  | "textAlignVertical"
>;

export type FieldInputProps = Omit<
  TextInputProps,
  "style" | "className" | "placeholderTextColor" | "selectionColor"
> & {
  /** Body is 14px; adaptive values are touch-safe; large entries retain 18px. */
  textRole?: "body" | "adaptive" | "large" | "unit" | "code" | "recovery";
  tone?: "default" | "onDark";
  /** When an outer disabled wrapper already dims the whole control. */
  dimWhenDisabled?: boolean;
  /** Only layout can be adjusted by a consumer. */
  layoutStyle?: StyleProp<FieldInputLayoutStyle>;
};

/**
 * Canonical editable text, shared by ordinary fields and specialized editors.
 * Compose with MoonFieldInput for a bordered field. This primitive forwards the
 * native input ref and editing props; it does not change parsing or saving.
 */
export const FieldInput = forwardRef<TextInput, FieldInputProps>(
  function FieldInput(
    {
      textRole = "body",
      tone = "default",
      layoutStyle,
      dimWhenDisabled = true,
      multiline,
      editable = true,
      ...props
    },
    ref,
  ) {
    const inputFontSize = useInputFontSize(
      textRole === "large" ? "large" : "adaptive",
    );
    const isCode = textRole === "code";
    const isRecovery = textRole === "recovery";
    const onDark = tone === "onDark";
    const fontSize =
      textRole === "large"
        ? inputFontSize
        : isCode
          ? onDark
            ? 22
            : 24
          : isRecovery
            ? 16
            : textRole === "adaptive" || textRole === "unit"
              ? onDark
                ? 16
                : inputFontSize
              : typography.sizes.md;

    return (
      <TextInput
        {...props}
        ref={ref}
        multiline={multiline}
        editable={editable}
        placeholderTextColor={
          onDark ? semanticColors.onDark.text.tertiary : colors.textFaint
        }
        selectionColor={onDark ? "rgba(255, 218, 189, 0.82)" : colors.warm}
        style={[
          {
            padding: 0,
            fontFamily:
              textRole === "unit"
                ? typography.families.sansMedium
                : typography.families.sans,
            fontSize,
            lineHeight: multiline
              ? fontSize * typography.lineHeights.normal
              : undefined,
            color: onDark
              ? semanticColors.onDark.text.primary
              : textRole === "unit"
                ? colors.textLight
                : colors.text,
            fontWeight: isCode || isRecovery ? "600" : "400",
            letterSpacing: isCode
              ? 8
              : isRecovery
                ? 1
                : textRole === "large"
                  ? 1.5
                  : 0,
            textAlign: isCode || isRecovery ? "center" : "left",
            textAlignVertical: multiline ? "top" : "center",
            opacity: !editable && dimWhenDisabled ? 0.6 : 1,
            ...(Platform.OS === "web"
              ? { outlineStyle: "none" as const, outlineWidth: 0 }
              : {}),
          },
          layoutStyle,
        ]}
      />
    );
  },
);
